import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../index"; // Firestore instance
import "./CardList.css";

const CACHE_KEY = "cardListCache";
const PRICE_CACHE_KEY = "priceCache";
const CACHE_TTL = 12 * 60 * 60 * 1000;

const CardList = () => {
  const [cards, setCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedRarities, setSelectedRarities] = useState([]);
  const [selectedSets, setSelectedSets] = useState([]);
  const [sortOption, setSortOption] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [error, setError] = useState(null);

  const fetchCardData = async () => {
    try {
      // Check for cached card data
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        if (Date.now() - timestamp < CACHE_TTL) {
          console.log("Using cached card list.");
          setCards(data);
          return;
        }
      }

      console.log("Fetching fresh card list.");
      const cardCollection = collection(db, "cards");
      const cardSnapshot = await getDocs(cardCollection);

      const cardList = [];
      for (const cardDoc of cardSnapshot.docs) {
        const cardData = cardDoc.data();
        const cardId = cardDoc.id;

        if (cardData.rarities) {
          for (const rarity of cardData.rarities) {
            cardList.push({
              id: cardId,
              ...cardData,
              rarity,
            });
          }
        } else {
          cardList.push({ id: cardId, ...cardData });
        }
      }

      // Fetch and cache price data
      const enrichedCardList = await Promise.all(
        cardList.map(async (card) => {
          const price = await fetchCardPrice(card.id, card.rarity);
          return { ...card, price };
        })
      );

      // Cache the data
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ timestamp: Date.now(), data: enrichedCardList })
      );

      setCards(enrichedCardList);
    } catch (error) {
      console.error("Error fetching cards from Firestore:", error);
      setError("Failed to load card data. Please try again later.");
    }
  };

  const fetchCardPrice = async (cardId, rarity) => {
    try {
      // Check for cached price data
      const cachedPriceData = localStorage.getItem(PRICE_CACHE_KEY);
      if (cachedPriceData) {
        const { timestamp, data } = JSON.parse(cachedPriceData);
        if (Date.now() - timestamp < CACHE_TTL) {
          console.log("Using cached price data.");
          return data[`${cardId}_${rarity}`];
        }
      }

      console.log("Fetching fresh price data.");
      const pricesRef = doc(db, `cards/${cardId}/prices`, rarity);
      const pricesSnapshot = await getDoc(pricesRef);

      if (pricesSnapshot.exists()) {
        const storePricesData = pricesSnapshot.data();
        let latestPrices = [];
        Object.keys(storePricesData).forEach((store) => {
          if (storePricesData[store].latestPrice) {
            latestPrices.push(storePricesData[store].latestPrice.price);
          }
        });

        const averagePrice =
          latestPrices.length > 0
            ? (
                latestPrices.reduce((sum, price) => sum + price, 0) /
                latestPrices.length
              ).toFixed(2)
            : "N/A";

        // Cache the price data
        const newCache = cachedPriceData
          ? {
              ...JSON.parse(cachedPriceData).data,
              [`${cardId}_${rarity}`]: averagePrice,
            }
          : { [`${cardId}_${rarity}`]: averagePrice };

        localStorage.setItem(
          PRICE_CACHE_KEY,
          JSON.stringify({ timestamp: Date.now(), data: newCache })
        );

        return averagePrice;
      }

      return "N/A";
    } catch (error) {
      console.error("Error fetching card price data:", error);
      return "N/A";
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  const handleSearch = (e) => setSearchQuery(e.target.value.toLowerCase());
  const toggleFilter = () => setIsFilterOpen(!isFilterOpen);

  const handleCheckboxChange = (setter, value, selectedValues) => {
    setter(
      selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value]
    );
  };

  const handleClearFilters = () => {
    setSelectedTypes([]);
    setSelectedRarities([]);
    setSelectedSets([]);
  };

  const searchFilter = (card) => {
    const type = card.type || "Unknown";
    const rarity = card.rarity || "Unknown";
    const set = card.set || "Unknown";

    const queryWords = searchQuery.split(" ").filter((word) => word);
    const cardText = `${card.name.toLowerCase()} ${card.text?.toLowerCase()}`;

    const matchesSearch = queryWords.every((word) => cardText.includes(word));
    const matchesType =
      selectedTypes.length === 0 || selectedTypes.includes(type);
    const matchesRarity =
      selectedRarities.length === 0 || selectedRarities.includes(rarity);
    const matchesSet = selectedSets.length === 0 || selectedSets.includes(set);

    return matchesSearch && matchesType && matchesRarity && matchesSet;
  };

  const filteredCards = cards.filter(searchFilter).sort((a, b) => {
    if (sortOption === "price-low-to-high") return a.price - b.price;
    if (sortOption === "price-high-to-low") return b.price - a.price;
    if (sortOption === "alpha") return a.name.localeCompare(b.name);
    return 0;
  });

  const uniqueTypes = [...new Set(cards.map((card) => card.type || "Unknown"))];
  const uniqueRarities = [
    ...new Set(cards.map((card) => card.rarity || "Unknown")),
  ];
  const uniqueSets = [...new Set(cards.map((card) => card.set || "Unknown"))];

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="card-list-page">
      <div className="controls-bar">
        <input
          type="text"
          placeholder="Search cards by name or text..."
          value={searchQuery}
          onChange={handleSearch}
          className="search-input"
        />
        <button className="filter-button" onClick={toggleFilter}>
          Filters
        </button>
        <select
          onChange={(e) => setSortOption(e.target.value)}
          value={sortOption}
          className="sort-select"
        >
          <option value="">Sort By</option>
          <option value="alpha">Alphabetical</option>
          <option value="price-low-to-high">Price: Low to High</option>
          <option value="price-high-to-low">Price: High to Low</option>
        </select>
      </div>
  
      <div className="content-container">
        <div className={`filter-sidebar ${isFilterOpen ? "open" : ""}`}>
          <div className="filter-section">
            <h3>Type</h3>
            {uniqueTypes.map((type) => (
              <button
                key={type}
                className={`filter-tag ${
                  selectedTypes.includes(type) ? "active" : ""
                }`}
                onClick={() => handleCheckboxChange(setSelectedTypes, type, selectedTypes)}
              >
                {type}
              </button>
            ))}
          </div>
          <div className="filter-section">
            <h3>Rarity</h3>
            {uniqueRarities.map((rarity) => (
              <button
                key={rarity}
                className={`filter-tag ${
                  selectedRarities.includes(rarity) ? "active" : ""
                }`}
                onClick={() =>
                  handleCheckboxChange(setSelectedRarities, rarity, selectedRarities)
                }
              >
                {rarity}
              </button>
            ))}
          </div>
          <div className="filter-section">
            <h3>Set</h3>
            {uniqueSets.map((set) => (
              <button
                key={set}
                className={`filter-tag ${
                  selectedSets.includes(set) ? "active" : ""
                }`}
                onClick={() =>
                  handleCheckboxChange(setSelectedSets, set, selectedSets)
                }
              >
                {set}
              </button>
            ))}
          </div>
          <button className="clear-filters-button" onClick={handleClearFilters}>
            Clear Filters
          </button>
        </div>
  
        <div className="card-list">
          {filteredCards.map((card) => (
            <Link
              to={`/card/${card.id}_${card.rarity}`}
              key={`${card.id}_${card.rarity}`}
              className="card-link"
            >
              <div className="card">
                <img
                  src={`/images/${card.id}_${card.rarity}.png`}
                  alt={card.name}
                  className="card-image"
                  loeading="lazy"
                  onError={(e) => (e.target.src = "/images/default.png")}
                />
                <div className="card-footer">
                  <span className="card-title">{card.name}</span>
                  <span
                    className={`card-rarity rarity-${card.rarity?.toLowerCase()}`}
                  >
                    {card.rarity}
                  </span>
                  <span className="card-price">
                    US$ {card.price || "N/A"}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );  
};

export default CardList;
